import { useEffect, useMemo, useState } from 'react';
import { Button, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import businessProAcademyImage from '../assets/businesspro-academy.png';
import dermoProAcademyImage from '../assets/dermopro-academy.png';
import leaderProAcademyImage from '../assets/leaderpro-academy.png';
import pharmaProAcademyImage from '../assets/pharmapro-academy.png';
import retailProAcademyImage from '../assets/retailpro-academy.png';

import { trainingService, toList } from '../services';
import { buildTrainingGroupPath, findTrainingGroupBySlug } from '../utils/trainingCatalog';

import '../styles/courses.css';

const toLines = (value) => String(value || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean);

const toDisplayTitleTr = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '';
  }

  const collapsedValue = rawValue.replace(/\s+/g, ' ');
  const isMostlyUppercase = collapsedValue === collapsedValue.toLocaleUpperCase('tr-TR');
  if (!isMostlyUppercase) {
    return collapsedValue;
  }

  const normalized = collapsedValue.toLocaleLowerCase('tr-TR');
  return normalized.charAt(0).toLocaleUpperCase('tr-TR') + normalized.slice(1);
};

const getAcademyLabel = (item) => {
  const audience = String(item?.audience || '').trim();
  if (audience && /academy/i.test(audience)) {
    return audience;
  }
  return '';
};

const shouldUseTrainingBrandLogo = (groupTitle) => {
  const normalizedGroupTitle = String(groupTitle || '').toLocaleLowerCase('tr-TR');
  return !normalizedGroupTitle.includes('sağlık ve kozmetik');
};

const TRAINING_BRAND_LOGOS = {
  dermopro: dermoProAcademyImage,
  pharmapro: pharmaProAcademyImage,
  retailpro: retailProAcademyImage,
  leaderpro: leaderProAcademyImage,
  businesspro: businessProAcademyImage,
};

const resolveTrainingBrandLogo = (item) => {
  const academyLabel = getAcademyLabel(item)
    .toLocaleLowerCase('tr-TR')
    .replace(/academy/g, '')
    .replace(/[^a-z0-9]+/g, '');

  if (academyLabel && TRAINING_BRAND_LOGOS[academyLabel]) {
    return TRAINING_BRAND_LOGOS[academyLabel];
  }

  const contentFingerprint = [item?.displayTitle, item?.summary, item?.displayAudience]
    .join(' ')
    .toLocaleLowerCase('tr-TR');

  if (contentFingerprint.includes('dermokozmetik')) {
    return dermoProAcademyImage;
  }

  if (contentFingerprint.includes('perakende')) {
    return retailProAcademyImage;
  }

  if (contentFingerprint.includes('iş geliştirme') || contentFingerprint.includes('is gelistirme')) {
    return businessProAcademyImage;
  }

  if (contentFingerprint.includes('liderlik') || contentFingerprint.includes('ekip yönetimi') || contentFingerprint.includes('ekip yonetimi')) {
    return leaderProAcademyImage;
  }

  if (contentFingerprint.includes('eczane yönetimi') || contentFingerprint.includes('eczane yonetimi')) {
    return pharmaProAcademyImage;
  }

  return null;
};

const AcademiesPage = () => {
  const { groupSlug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadItems = async () => {
      try {
        const response = await trainingService.getItems();
        if (active) {
          setItems(toList(response.data));
        }
      } catch (error) {
        if (active) {
          setItems([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadItems();

    return () => {
      active = false;
    };
  }, []);

  const groupedItems = useMemo(() => items.reduce((accumulator, item) => {
    const groupTitle = String(item.group_title || 'Diğer Eğitimler').trim() || 'Diğer Eğitimler';
    if (!accumulator[groupTitle]) {
      accumulator[groupTitle] = [];
    }
    accumulator[groupTitle].push({
      ...item,
      displayTitle: toDisplayTitleTr(item.title),
      contentLines: toLines(item.content),
      displayAudience: toDisplayTitleTr(item.audience),
    });
    return accumulator;
  }, {}), [items]);

  const groupEntries = useMemo(() => Object.entries(groupedItems), [groupedItems]);
  const selectedGroupEntry = useMemo(() => findTrainingGroupBySlug(groupEntries, groupSlug), [groupEntries, groupSlug]);
  const selectedGroupTitle = selectedGroupEntry?.[0] || '';
  const selectedGroupItems = selectedGroupEntry?.[1] || [];

  return (
    <div className="academies-page-shell">
      <div className="page-shell academies-shell">
        <section className="academies-hero academies-hero--trainings">
          <div>
            <p className="academies-kicker">Syntera Eğitim ve Danışmanlık</p>
            <h1>Eğitimlerimiz</h1>
            <p>
              Sektör odaklı eğitim çözümlerimizi alan bazında inceleyebilir, kurumunuza veya ekibinize en
              uygun programlara buradan ulaşabilirsiniz.
            </p>
            <div className="academies-hero-actions">
              <Link to="/register">
                <Button type="primary" size="large">Kayıt Ol</Button>
              </Link>
              <Link to="/iletisim">
                <Button size="large" className="transparent-contact-button">Bize Ulaşın</Button>
              </Link>
            </div>
          </div>
        </section>

        {loading ? (
          <section className="academies-placeholder surface-card academies-loading-state">
            <Spin size="large" />
          </section>
        ) : items.length === 0 ? (
          <section className="academies-placeholder surface-card">
            <div className="academies-placeholder-badge">İçerik Bekleniyor</div>
            <h2>Eğitim İçerikleri Yakında Burada</h2>
            <p>
              Yönetim panelinden ilk eğitim kaydını eklediğinizde bu alan alt alta eğitim kartlarıyla
              dolacak. Başlık, açıklama ve görseller sonradan kolayca güncellenebilir.
            </p>
            <div className="academies-placeholder-actions">
              <Link to="/">
                <Button type="primary">Ana Sayfaya Dön</Button>
              </Link>
            </div>
          </section>
        ) : groupSlug && !selectedGroupEntry ? (
          <section className="academies-placeholder surface-card">
            <div className="academies-placeholder-badge">Alan Bulunamadı</div>
            <h2>Bu eğitim alanı bulunamadı</h2>
            <p>Üst menüden geçerli bir eğitim alanı seçerek devam edebilirsiniz.</p>
            <div className="academies-placeholder-actions">
              <Link to="/egitimlerimiz">
                <Button type="primary">Tüm Alanlara Dön</Button>
              </Link>
            </div>
          </section>
        ) : groupSlug ? (
          <section className="academies-group-page">
            <div className="academies-group-page-head">
              <Link to="/egitimlerimiz" className="academies-back-link">
                <ArrowLeftOutlined /> Tüm Kategoriler
              </Link>
              <div className="academies-group-banner">{toDisplayTitleTr(selectedGroupTitle)}</div>
            </div>
            <div className="academies-training-group-grid academies-training-group-grid--wide">
              {selectedGroupItems.map((item) => {
                const showBrandLogo = shouldUseTrainingBrandLogo(selectedGroupTitle);
                const brandLogo = showBrandLogo ? resolveTrainingBrandLogo(item) : null;
                const hasCoverImage = Boolean(item.cover_image);
                const academyLabel = showBrandLogo ? getAcademyLabel(item) : '';

                return (
                <article key={item.id} className={`surface-card academies-training-card${hasCoverImage ? '' : ' is-text-only'}`}>
                  <div className={`academies-training-grid${hasCoverImage ? '' : ' no-media'}`}>
                    {hasCoverImage ? (
                      <div className="academies-training-media">
                        <img
                          src={item.cover_image}
                          alt={item.displayTitle}
                          className="academies-training-image"
                        />
                      </div>
                    ) : null}
                    <div className="academies-training-copy">
                      <div className="academies-training-heading-row">
                        <div className="academies-training-heading-main">
                          {brandLogo ? (
                            <img
                              src={brandLogo}
                              alt={academyLabel || item.displayTitle}
                              className="academies-training-brand-logo"
                            />
                          ) : null}
                          <h2>{item.displayTitle}</h2>
                        </div>
                        {item.duration_label ? <span className="academies-duration-pill">{item.duration_label}</span> : null}
                      </div>
                      {item.audience && !/academy/i.test(item.audience) ? <p className="academies-training-audience">{item.displayAudience}</p> : null}
                      {item.summary ? <p className="academies-training-summary">{item.summary}</p> : null}
                      <ul className="academies-training-points">
                        {item.contentLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              );})}
            </div>
          </section>
        ) : (
          <section className="academies-overview-grid">
            {groupEntries.map(([groupTitle, groupItems]) => (
              <Link key={groupTitle} to={buildTrainingGroupPath(groupTitle)} className="academies-overview-card surface-card">
                <div className="academies-placeholder-badge">Eğitim</div>
                <h2>{toDisplayTitleTr(groupTitle)}</h2>
                <p>Bu alandaki tüm eğitimleri tek sayfada görüntüleyin.</p>
                <div className="academies-overview-meta">
                  <span>{groupItems.length} eğitim</span>
                  <span>Detayı Aç</span>
                </div>
              </Link>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default AcademiesPage;